import { useMemo } from "react";
import { CheckCircle, Fire } from "@phosphor-icons/react";
import { OverviewCard } from "./Card";

/**
 * Widget #3 — Allenamenti completati.
 *
 * Un workout è "completato" su una data quando il cliente ha loggato
 * almeno un esercizio della scheda in quella data — coerente con la
 * dashboard cliente.
 */

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isoDate(date) {
  return startOfDay(date).toISOString().split("T")[0];
}

export function CompletedWorkoutsWidget({ logs, workouts }) {
  const stats = useMemo(() => {
    const allLogs = logs || [];
    // Total: unique (workout_id|date) pairs across history.
    const uniqueAll = new Set();
    const uniqueLast4Weeks = new Set();
    const uniqueThisWeek = new Set();
    const today = new Date();
    const startWeek = new Date(today);
    const dow = today.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    startWeek.setDate(today.getDate() + mondayOffset);
    startWeek.setHours(0, 0, 0, 0);
    const since4w = new Date(today);
    since4w.setDate(today.getDate() - 28);
    since4w.setHours(0, 0, 0, 0);

    allLogs.forEach((l) => {
      if (!l.workout_id || !l.date) return;
      const key = `${l.workout_id}|${l.date}`;
      uniqueAll.add(key);
      const d = startOfDay(l.date);
      if (d >= since4w) uniqueLast4Weeks.add(key);
      if (d >= startWeek) uniqueThisWeek.add(key);
    });

    // Adherence over the last 4 weeks vs expected count of weekly workouts.
    const weeklyExpected = (workouts || []).length;
    const expected4w = weeklyExpected * 4;
    const adherence = expected4w > 0
      ? Math.min(100, Math.round((uniqueLast4Weeks.size / expected4w) * 100))
      : null;

    return {
      total: uniqueAll.size,
      last4w: uniqueLast4Weeks.size,
      thisWeek: uniqueThisWeek.size,
      weeklyExpected,
      adherence,
      lastDate: allLogs.length ? isoDate(new Date(allLogs.reduce((m, l) => l.date > m ? l.date : m, allLogs[0].date))) : null,
    };
  }, [logs, workouts]);

  return (
    <OverviewCard
      title="Allenamenti completati"
      icon={CheckCircle}
      iconColor="text-[#007AFF]"
      testId="overview-completed-workouts"
    >
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-2 rounded-lg bg-[#171717] border border-[#1F1F1F]">
          <p className="text-2xl font-black text-white tabular-nums" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {stats.total}
          </p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Totali</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-[#171717] border border-[#1F1F1F]">
          <p className="text-2xl font-black text-white tabular-nums" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {stats.last4w}
          </p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Ultime 4 sett.</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-[#171717] border border-[#1F1F1F]">
          <p className="text-2xl font-black text-[#D4FF00] tabular-nums flex items-center justify-center gap-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            <Fire size={16} weight="fill" /> {stats.thisWeek}
          </p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Settimana</p>
        </div>
      </div>
      {stats.adherence != null && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-1">
            <span>Aderenza (4 sett.)</span>
            <span className="text-white font-semibold">{stats.adherence}%</span>
          </div>
          <div className="relative h-1.5 rounded-full bg-[#1A1A1A] overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#34C759] rounded-full transition-all"
              style={{ width: `${stats.adherence}%` }}
            />
          </div>
        </div>
      )}
    </OverviewCard>
  );
}
